import { amorphicStatic } from '@haventech/amorphic';
import jwt from 'jsonwebtoken';
import axios from 'axios';
import { LaunchDarklyInstance, LD_APP } from '../launch-darkly/LaunchDarklySingleton';
import { FeatureFlags } from '../../../coverpath-common/js/interfaces/CoverpathTypes';
import { serverOnly } from '../helpers';
import { LoggerSingleton } from '../logger/LoggerSingleton';
import * as _ from 'lodash';
import { RestJsonWsClient } from './RestJsonWsClient';
import { MetadataHierarchy } from '../metadata/MetadataHierarchy';
import { Utils } from '../Utils';
import { UICategory, UILoggerSingleton } from '../logger/UILoggerSingleton';

interface DocuSignTokenResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
}

interface DocuSignToken {
  accessToken: string;
  expirationDate: number;
}

export class DocuSignWebClient extends RestJsonWsClient {
  static token: DocuSignToken;

  async _addHeaders(headers) {
    super._addHeaders(headers);
    _.extend(headers, { ...LoggerSingleton.getInstance()?.getHeaderContextFromLocalStorage?.() });

    const isDocuSignOAuthEnabled = await this.getIsDocuSignOAuthEnabled('DocuSignWebClient');

    if (isDocuSignOAuthEnabled) {
      // CurrentTime + 10 minutes to add some buffer for refresh
      const currTime = Math.floor(Date.now() / 1000) + 600;

      if (!DocuSignWebClient.token || (DocuSignWebClient.token && DocuSignWebClient.token.expirationDate <= currTime)) {
        DocuSignWebClient.token = await this.getAccessToken();
      }

      headers['Authorization'] = `Bearer ` + DocuSignWebClient.token.accessToken;

      return;
    }

    //this can be empty if we use VoidDocuSignClient and other generic workflow activity
    const hierarchy = this.serviceConfig.hierarchy;
    let sendOnBehalfOf;
    if (hierarchy) {
      const channelName = Utils.getAppName(MetadataHierarchy.getOptions(hierarchy).channel.toLowerCase());
      sendOnBehalfOf = amorphicStatic.config.configStore[channelName].get('docusignSendOnBehalfOf');
    }

    headers['X-DocuSign-Authentication'] = JSON.stringify({
      Username: this.serviceConfig.wsUsername,
      Password: this.serviceConfig.wsPassword,
      IntegratorKey: this.serviceConfig.integratorKey,
      SendOnBehalfOf: sendOnBehalfOf,
    });

    if (!sendOnBehalfOf) {
      delete headers['X-DocuSign-Authentication'].SendOnBehalfOf;
    }

    return;
  }

  async getAccessToken(serviceConfig?): Promise<DocuSignToken | null> {
    if (!serviceConfig) {
      serviceConfig = this.serviceConfig;
    }
    // Create token
    const body = {
      iss: serviceConfig.integrationKey,
      sub: serviceConfig.userId,
      aud: serviceConfig.OAuthUri,
      iat: Math.floor(new Date().getTime() / 1000),
      exp: Math.floor(new Date().getTime() / 1000) + 6000,
      scope: 'signature impersonation',
    };

    const privateKey = Buffer.from(serviceConfig.OAuthPrivateKey, 'base64').toString('ascii');
    const jwtToken = await jwt.sign(body, privateKey, { algorithm: 'RS256' });

    // Get access token
    const OAuthUrl = 'https://' + serviceConfig.OAuthUri + '/oauth/token';
    const OAuthGrant = `grant_type=urn:ietf:params:oauth:grant-type:jwt-bearer&assertion=${jwtToken}`;

    try {
      const resp = await axios.post(OAuthUrl, OAuthGrant, {
        timeout: 30000,
        headers: {
          name: 'content-type',
          value: 'application/x-www-form-urlencoded',
        },
      });
      return this.buildTokenResponse(resp.data);
    } catch (e) {
      const err = {
        name: e?.name,
        message: e?.message,
        stack: e?.stack,
        data: {
          url: OAuthUrl,
        },
      };
      UILoggerSingleton.getInstance().error(
        {
          module: 'DocuSignWebClient',
          function: 'getAccessToken',
          category: UICategory.request,
          error: err,
        },
        'An error occurred when trying to retrieve DocuSign access token'
      );
      return null;
    }
  }

  private buildTokenResponse(response: DocuSignTokenResponse): DocuSignToken {
    return {
      expirationDate: Math.floor(Date.now() / 1000) + response.expires_in,
      accessToken: response.access_token,
    };
  }

  @serverOnly()
  async getIsDocuSignOAuthEnabled(userId: string): Promise<boolean> {
    const ldInstance = await LaunchDarklyInstance.getInstance(LD_APP.cp);
    return ldInstance.getFeatureFlagValueAsBoolean(FeatureFlags.isDocuSignOAuthEnabled, userId);
  }
}
